import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import AdminPanel, { PasscodeModal, loadPortfolioData, savePortfolioData, resetPortfolioData } from './AdminPanel';

export default function Portfolio() {
  const [portfolioData, setPortfolioData] = useState(() => loadPortfolioData());
  const [showPasscode, setShowPasscode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const updateData = useCallback((newData) => {
    setPortfolioData(newData);
    savePortfolioData(newData);
  }, []);

  const handleReset = useCallback(() => {
    const defaults = resetPortfolioData();
    setPortfolioData(defaults);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          setMousePosition(mouseRef.current);
          rafRef.current = null;
        });
      }
    };

    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress((window.scrollY / totalScroll) * 100);

      const sections = ['home', 'about', 'projects', 'skills', 'contact'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const particles = Array.from({ length: 35 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        size: Math.random() * 1.5 + 0.5,
      }));

      let animId;
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particles.forEach((p, i) => {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
          if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
          ctx.fillStyle = 'rgba(109,213,110,0.45)';
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
          for (let j = i + 1; j < particles.length; j++) {
            const dx = p.x - particles[j].x;
            const dy = p.y - particles[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 130) {
              ctx.strokeStyle = `rgba(109,213,110,${0.1 * (1 - dist / 130)})`;
              ctx.lineWidth = 0.5;
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
            }
          }
        });
        animId = requestAnimationFrame(animate);
      };
      animate();

      const onResize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
      window.addEventListener('resize', onResize);
      return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', onResize); };
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const { hero, about, projects, skills, contact, footer } = portfolioData;

  const navLinks = ['home', 'about', 'projects', 'skills', 'contact'];

  return (
    <div className="portfolio">
      <canvas ref={canvasRef} className="particle-canvas" />

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${scrollProgress}%` }} />
      </div>

      <div className="cursor-glow" style={{ left: mousePosition.x, top: mousePosition.y }} />

      <nav className="navbar">
        <div className="nav-inner">
          <span className="nav-logo" onClick={() => scrollTo('home')}>EM<button className="admin-trigger" onClick={(e) => { e.stopPropagation(); setShowPasscode(true); }} aria-label="Admin" /></span>
          <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
            {navLinks.map(link => (
              <li key={link}>
                <button
                  className={`nav-link ${activeSection === link ? 'active' : ''}`}
                  onClick={() => { scrollTo(link); setMenuOpen(false); }}
                >
                  {link.charAt(0).toUpperCase() + link.slice(1)}
                </button>
              </li>
            ))}
          </ul>
          <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
            <span className={menuOpen ? 'bar open' : 'bar'} />
            <span className={menuOpen ? 'bar open' : 'bar'} />
            <span className={menuOpen ? 'bar open' : 'bar'} />
          </button>
        </div>
      </nav>

      <section className="hero" id="home">
        <div className="hero-content">
          <div className="hero-grid">
            <div className="hero-left">
              <span className="hero-badge">{hero.badge}</span>
              <h1 className="hero-title">
                {hero.firstName}<br />
                <span className="hero-title-accent">{hero.lastName}</span>
              </h1>
              <p className="hero-role">
                <span className="role-bracket">&lt;</span>
                {hero.role}
                <span className="role-bracket"> /&gt;</span>
              </p>
              <p className="hero-description">{hero.description}</p>
              <div className="hero-cta">
                <a href={hero.githubUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  <GithubIcon /> GitHub
                </a>
                <a href={hero.linkedinUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline">
                  <LinkedInIcon /> LinkedIn
                </a>
              </div>
            </div>
            <div className="hero-right">
              <div className="photo-frame">
                <div className="photo-ring r1" />
                <div className="photo-ring r2" />
                <img src={hero.photoUrl} alt={`${hero.firstName} ${hero.lastName}`} className="hero-photo" />
              </div>
            </div>
          </div>
        </div>
        <button className="scroll-hint" onClick={() => scrollTo('about')}>
          <span>Scroll</span>
          <div className="scroll-dot" />
        </button>
      </section>

      <section className="about" id="about">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{'// '}About Me</span>
            <h2 className="section-title">Building the Future,<br />One Line at a Time</h2>
          </div>
          <div className="about-grid">
            <div className="about-text">
              <p className="lead">{about.lead}</p>
              <p>{about.paragraph1}</p>
              <p>{about.paragraph2}</p>
              <div className="stats-row">
                {about.stats.map((s) => (
                  <div className="stat-card" key={s.label}>
                    <div className="stat-value">{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="code-window">
              <div className="window-bar">
                <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                <span className="window-title">Developer.java</span>
              </div>
              <pre className="window-code">{about.codeWindow}</pre>
            </div>
          </div>
        </div>
      </section>

      <section className="projects" id="projects">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{'// '}Featured Work</span>
            <h2 className="section-title">Projects That Matter</h2>
          </div>
          <div className="projects-grid">
            {projects.map((p, i) => (
              <div className="project-card" key={i} style={{ '--card-color': p.color }}>
                <div className="card-top-bar" />
                <div className="project-body">
                  <div className="project-meta">
                    <h3 className="project-name">{p.title}</h3>
                    <span className="project-tech">{p.tech}</span>
                  </div>
                  <p className="project-desc">{p.description}</p>
                  <div className="project-tags">
                    {p.highlights.map((h, j) => <span className="tag" key={j}>{h}</span>)}
                  </div>
                  <div className="project-actions">
                    {p.github && (
                      <a href={p.github} target="_blank" rel="noopener noreferrer" className="action-link">
                        <GithubIcon /> Source Code
                      </a>
                    )}
                    {p.live && (
                      <a href={p.live} target="_blank" rel="noopener noreferrer" className="action-link">
                        <ExternalIcon /> Live Demo
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="skills" id="skills">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{'// '}Technical Arsenal</span>
            <h2 className="section-title">Skills & Technologies</h2>
          </div>
          <div className="skills-wrapper">
            {Object.entries(skills).map(([cat, items]) => (
              <div className="skill-group" key={cat}>
                <h3 className="group-title">{cat}</h3>
                <div className="skill-grid">
                  {items.map((s, i) => (
                    s.type === 'badge' ? (
                      <div className="skill-badge" key={i}>{s.name}</div>
                    ) : (
                      <div className="skill-item" key={i}>
                        {s.type === 'svg' && s.svgMarkup
                          ? <div className="skill-img-wrap" dangerouslySetInnerHTML={{ __html: s.svgMarkup }} />
                          : <img src={s.icon} alt={s.name} className="skill-img" />}
                        <span className="skill-label">{s.name}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="contact" id="contact">
        <div className="container">
          <div className="contact-inner">
            <span className="section-tag centered">{'// '}Get In Touch</span>
            <h2 className="section-title centered">Let's Build Something Amazing</h2>
            <p className="contact-sub">{contact.subtitle}</p>
            <div className="contact-actions">
              <a href={`mailto:${contact.email}`} className="btn btn-primary large">
                <EmailIcon /> {contact.email}
              </a>
              <div className="social-row">
                <a href={contact.githubUrl} target="_blank" rel="noopener noreferrer" className="social-btn" title="GitHub">
                  <GithubIcon />
                </a>
                <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" className="social-btn" title="LinkedIn">
                  <LinkedInIcon />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <p>{footer.text}</p>
          <p className="footer-quote">{footer.quote}</p>
        </div>
      </footer>

      {showPasscode && (
        <PasscodeModal
          onUnlock={() => { setIsAdmin(true); setShowPasscode(false); }}
          onClose={() => setShowPasscode(false)}
        />
      )}
      {isAdmin && (
        <AdminPanel
          data={portfolioData}
          onChange={updateData}
          onClose={() => setIsAdmin(false)}
          onReset={handleReset}
        />
      )}
    </div>
  );
}

const GithubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);
